import { supportHeaders, supportContext } from '../support/context';
/**
 * Clerk wiring for Voyager, gated on the publishable key so the app stays
 * fully functional when Clerk is not configured (local dev without keys, CI).
 * When the key is absent AuthGate renders the app exactly as before; when
 * present it requires a signed-in user. Mirrors the backend's env-gated
 * middleware (CLERK_SECRET_KEY) and the pattern proven in sidira-simulator.
 */
import React, { useEffect, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import {
  ClerkProvider,
  SignedIn as ClerkSignedIn,
  SignedOut as ClerkSignedOut,
  SignIn,
  useAuth,
} from '@clerk/clerk-react';

import { setPersistenceNamespace } from '../stores/helpers/persistenceHelpers';
import { CLERK_APPEARANCE, CLERK_JS_VERSION } from './clerkAppearance';

// Clerk v5 types its control components as returning ReactNode (React 19
// style), which TS 4.9 (CRA's ceiling) rejects as a JSX component type.
// Re-type them locally; runtime behavior is unchanged.
const SignedIn = ClerkSignedIn as unknown as React.FC<{ children: ReactNode }>;
const SignedOut = ClerkSignedOut as unknown as React.FC<{ children: ReactNode }>;

export const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY ?? '';

export const clerkEnabled = (): boolean => CLERK_PUBLISHABLE_KEY.length > 0;

// ---------------------------------------------------------------------------
// Session token access for non-React code (axios interceptor, WS slices).
//
// Clerk session tokens are short-lived (~60s). TokenBridge stores the live
// getToken() in module scope and keeps a cached copy fresh for the WebSocket
// connect paths, which are synchronous and cannot await.
// ---------------------------------------------------------------------------

type TokenGetter = (opts?: { skipCache?: boolean }) => Promise<string | null>;

let _getToken: TokenGetter | null = null;
let _cachedToken: string | null = null;
let _activeAccountIdentity: string | null = null;
const AccountIdentityContext = React.createContext<string | null>(null);

export function getActiveAccountIdentity(): string | null {
  return _activeAccountIdentity;
}

export function useAccountIdentity(): string | null {
  return React.useContext(AccountIdentityContext);
}

/**
 * Close code the backend uses to reject a WebSocket handshake it could not
 * authenticate (see `agent/common/ws_auth.reject_unauthorized`). Distinct from
 * the 1006 a browser reports for every other failed connect, so the client can
 * retry with a fresh token instead of guessing the server is down.
 */
export const WS_AUTH_REJECTED_CODE = 4401;

/**
 * How close to `exp` a token has to be before we refuse to hand it out.
 * Wide enough to cover the connect it is about to authorize plus clock skew.
 */
const STALE_MARGIN_MS = 15_000;

/** `exp` in epoch ms, or 0 when the token is absent or unreadable. */
function expiresAt(token: string | null): number {
  if (!token) return 0;
  const parts = token.split('.');
  if (parts.length !== 3) return 0;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json)?.exp;
    return typeof exp === 'number' ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

function isStale(token: string | null): boolean {
  const exp = expiresAt(token);
  return exp > 0 && exp - Date.now() <= STALE_MARGIN_MS;
}

/** Fresh session token for HTTP requests (null when Clerk is disabled/signed out). */
export async function getClerkToken(): Promise<string | null> {
  if (!_getToken) return null;
  try {
    let token = await _getToken();
    // getToken() serves Clerk's OWN cache and only re-mints within ~10s of
    // expiry, so asking on a schedule hands back the same ~60s JWT until it
    // is nearly dead. Force a mint when what came back is already too close
    // to the edge to survive the request it is meant to authorize —
    // otherwise the WS cache below can hold an outright expired token.
    if (isStale(token)) token = await _getToken({ skipCache: true });
    _cachedToken = token;
    return token;
  } catch {
    return null;
  }
}

/**
 * Authorization header for native fetch() calls that bypass the axios client.
 * Empty when Clerk is disabled or signed out.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await getClerkToken();
  return { ...supportHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

/**
 * Append the Clerk session token to a WebSocket URL (`?token=…` / `&token=…`),
 * from the cached token — WS connects are synchronous. Query-aware: deployed
 * env URLs already carry `?token=<shared WS_AUTH_TOKEN>` (see
 * deploy/quasar/quasar-deploy.sh), so the Clerk token is added with `&` and
 * the backend gate checks every `token` value. No-op when Clerk is disabled.
 */
export function withWsAuth(url: string): string {
  url += `${url.includes('?') ? '&' : '?'}support_page=${supportContext().page_id}`;
  if (!clerkEnabled()) return url;
  // Timer throttling in backgrounded tabs can leave the cache stale past the
  // ~60s JWT lifetime. Kick off a refresh so a rejected handshake's retry
  // (see WS_AUTH_REJECTED_CODE) gets a fresh token; the visibilitychange
  // listener in TokenBridge covers the common wake-from-sleep path before
  // user action. This call cannot be awaited — WS connects are synchronous —
  // so the retry, not this line, is what recovers a stale token.
  getClerkToken().catch(() => {});
  if (!_cachedToken) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(_cachedToken)}`;
}

function TokenBridge({ children }: { children: ReactNode }) {
  const { getToken, userId, orgId } = useAuth();
  const accountIdentity = userId || null;
  _activeAccountIdentity = accountIdentity;

  // Scope localStorage question history (threads/spaces) to this account AND
  // the active organization (#48) — the org switcher changes the workspace,
  // so personal and per-org histories must not bleed into each other.
  useEffect(() => {
    const ns = userId ? (orgId ? `${userId}:${orgId}` : userId) : null;
    setPersistenceNamespace(ns);
  }, [userId, orgId]);

  // Assign during render, not in the effect: children render (and fire their
  // mount effects) BEFORE a parent's effects run, so an effect-time assignment
  // would let the app's first API calls go out with no getter registered —
  // 401s with no retry. Parent render precedes child render, so this is the
  // one spot that deterministically runs first. Idempotent, so safe to repeat.
  _getToken = getToken;

  useEffect(() => {
    // Also assign in effect setup: Strict Mode replays effects as
    // setup → cleanup → setup with no re-render in between, and the cleanup
    // nulls the getter — without this line the replayed setup would leave
    // every subsequent request unauthenticated in dev.
    _getToken = getToken;
    _activeAccountIdentity = accountIdentity;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Drive the schedule off the token's own `exp` rather than a fixed
    // interval. A fixed interval shorter than the lifetime is not enough:
    // getToken() returns the SAME JWT until it is nearly expired, so a 45s
    // poll against a 60s token re-mints on every OTHER tick and leaves a
    // ~30s window where the cache holds an expired token — which is exactly
    // what a WS handshake then presents. Re-minting at two-thirds of the
    // remaining life keeps the cache ahead of expiry at every tick.
    function schedule(token: string | null) {
      if (cancelled) return;
      const remaining = expiresAt(token) - Date.now();
      const delay = remaining > 0 ? Math.max(5_000, Math.floor(remaining * 2 / 3)) : 20_000;
      timer = setTimeout(refresh, delay);
    }

    function refresh() {
      if (timer) { clearTimeout(timer); timer = null; }
      getClerkToken().then(schedule, () => schedule(null));
    }

    refresh();
    // Backgrounded tabs throttle timers past the token lifetime — refresh
    // immediately on foreground so the next WS connect has a live token.
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      _getToken = null;
      _cachedToken = null;
      if (_activeAccountIdentity === accountIdentity) _activeAccountIdentity = null;
    };
  }, [getToken, accountIdentity]);

  return <AccountIdentityContext.Provider value={accountIdentity}>{children}</AccountIdentityContext.Provider>;
}

// ---------------------------------------------------------------------------
// AuthGate — mounted inside the app theme so signed-out surfaces share its tokens
// ---------------------------------------------------------------------------

export function AuthGate({ children }: { children: ReactNode }) {
  if (!clerkEnabled()) return <AccountIdentityContext.Provider value={null}>{children}</AccountIdentityContext.Provider>;

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      clerkJSVersion={CLERK_JS_VERSION}
      appearance={CLERK_APPEARANCE}
    >
      <SignedIn>
        <TokenBridge>{children}</TokenBridge>
      </SignedIn>
      <SignedOut>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            bgcolor: 'var(--ground)',
          }}
        >
          {/* hash routing: this SPA has no client-side router, and Clerk v5's
              default path-based routing throws without one. */}
          <SignIn routing="hash" forceRedirectUrl={`${window.location.origin}${window.location.pathname}${window.location.search}`} />
        </Box>
      </SignedOut>
    </ClerkProvider>
  );
}
